import { useState } from "react";
import { Link } from "react-router";

import { useProjectOverviewQuery } from "@/api/hooks";
import {
  classifySignoffFailure,
  useCreateSignoffMutation,
} from "@/api/publication";
import { labBackPath } from "@/lib/labUrl";
import { lifecycleRefusalCopy } from "@/lib/lifecycle";
import { candidateDisplayVersion } from "@/lib/previewInsights";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { SIGNOFF_FAILURE_COPY } from "@/ui/publication/PublicationLane";

// Refusals that mean "this account or Project never offers acceptance": no button.
const HIDDEN_REASONS = new Set<string>([
  "lifecycle.capability_missing",
  "lifecycle.project_archived",
  "lifecycle.candidate_missing",
]);

const NOT_SELECTED_REASON =
  "Approval is for the current test version. Switch to it to approve.";

/** Clinical sign-off for the Project's current frozen test version, from the Lab. */
export function LabAcceptPreview({
  projectId,
  selectedCandidateId = null,
}: {
  projectId: string;
  /** The test version chosen in the Lab's top bar; approving another one is refused here. */
  selectedCandidateId?: string | null;
}) {
  const overview = useProjectOverviewQuery(projectId, {
    pollWhileAwaitingPreview: true,
  });
  const signoff = useCreateSignoffMutation(projectId);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [asking, setAsking] = useState(false);
  const [approved, setApproved] = useState(false);

  const lifecycle = overview.data?.lifecycle;
  const identity = lifecycle?.candidate?.identity;
  const version = candidateDisplayVersion(lifecycle?.candidate?.revision);
  const verdict = lifecycle?.capabilities.acceptCandidate;
  const status = overview.data?.publicationStatus;
  const accepted =
    status?.candidateAcceptance === "accepted" &&
    status.acceptedCandidateId === identity?.candidateId;

  if (!notice && (!identity || !verdict)) return null;
  if (
    !notice &&
    (accepted ||
      (verdict?.state === "refused" && HIDDEN_REASONS.has(verdict.reason)))
  ) {
    return null;
  }

  const reason =
    verdict?.state === "refused"
      ? lifecycleRefusalCopy(verdict.reason)
      : selectedCandidateId !== null &&
          identity &&
          selectedCandidateId !== identity.candidateId
        ? NOT_SELECTED_REASON
        : null;

  // Same guard as the Preview dialog, run when the dialog opens and again when
  // Approve is pressed: re-read the Project; true only while the exact
  // candidate this button was rendered for is still current and acceptable.
  async function stillApprovable(): Promise<boolean> {
    if (!identity) return false;
    const fresh = await overview.refetch().catch(() => null);
    const current = fresh?.data?.lifecycle;
    if (!fresh || fresh.isError || !current) {
      setNotice(
        "Whether this version can be accepted could not be confirmed. Reload the project and try again.",
      );
      return false;
    }
    const now = current.candidate?.identity;
    if (
      now?.candidateId !== identity.candidateId ||
      now.contentHash !== identity.contentHash ||
      now.basePackSha !== identity.basePackSha
    ) {
      setNotice(
        "This is no longer the project's current test version. Review and preview the current version before accepting it.",
      );
      return false;
    }
    const refreshed = current.capabilities.acceptCandidate;
    if (refreshed.state === "refused") {
      setNotice(lifecycleRefusalCopy(refreshed.reason));
      return false;
    }
    return true;
  }

  async function startApproval() {
    if (!identity || confirming || signoff.isPending) return;
    setNotice(null);
    setApproved(false);
    setConfirming(true);
    const ok = await stillApprovable();
    setConfirming(false);
    if (ok) setAsking(true);
  }

  async function approve() {
    if (!identity || confirming) return;
    setConfirming(true);
    const ok = await stillApprovable();
    setConfirming(false);
    if (!ok) {
      setAsking(false);
      return;
    }
    try {
      await signoff.mutateAsync({
        candidate: {
          candidateId: identity.candidateId,
          contentHash: identity.contentHash,
          basePackSha: identity.basePackSha,
        },
      });
      setAsking(false);
      setApproved(true);
      setNotice("Clinically approved");
    } catch (error) {
      setAsking(false);
      setNotice(SIGNOFF_FAILURE_COPY[classifySignoffFailure(error).kind]);
    }
  }

  return (
    <div className="flex flex-col items-start gap-1">
      {identity && !accepted ? (
        <Button
          size="sm"
          variant="outline"
          disabled={signoff.isPending || confirming || reason !== null}
          title={reason ?? undefined}
          aria-describedby={reason === null ? undefined : "lab-accept-reason"}
          onClick={() => void startApproval()}
        >
          {signoff.isPending || confirming
            ? "Approving…"
            : "Approve this version"}
        </Button>
      ) : null}
      {reason !== null && !notice ? (
        <span id="lab-accept-reason" className="sr-only">
          {reason}
        </span>
      ) : null}
      {notice ? (
        <span role="status" className="text-xs">
          {notice}
        </span>
      ) : null}
      {approved ? (
        <Link
          to={labBackPath(projectId)}
          className="text-xs underline underline-offset-2"
        >
          Back to project to send for review
        </Link>
      ) : null}
      <Dialog
        open={asking}
        onOpenChange={(open) => {
          if (!open && !signoff.isPending) setAsking(false);
        }}
      >
        <DialogContent
          title={`Approve ${version ?? "this test version"} clinically?`}
          description={`This approves this exact test version of ${overview.data?.project.name ?? "this project"}. Next: send it for review from the project page. Approving does not publish it.`}
          busy={signoff.isPending || confirming}
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => setAsking(false)}
                disabled={signoff.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => void approve()}
                disabled={signoff.isPending || confirming}
              >
                Approve
              </Button>
            </>
          }
        />
      </Dialog>
    </div>
  );
}
